<template>
  <div class="height-form">
    <div class="flex flex-col h-full">
      <div class="flex-grow h-full outline-none">
        <TextEditor
          :savedContent="currentSubmission.prompt_response !== null ? currentSubmission.prompt_response : currentSubmission.draft_response"
          @change="handleResponseChange"
          @editor="editor = $event"
        />
      </div>
      <div class="h-16 px-3.5 flex items-center justify-between bg-white border-t border-solid border-gray-200">
        <div>
          <div v-if="getCurrentRT.is_genie_feedback_enable === true && hasContent" class="flex items-center space-x-2">
            <div class="flex items-center space-x-2.5">
              <Tooltip text="Genie Feedback" positionTooltip="relative" slot="reference" backgroundColor="bg-gray-700" class="flex items-center">
                <button @click="handleGenieFeedback()" class="w-7 h-7 rounded-full bg-gray-700 relative">
                  <div v-if="show_feedback === true" class="text-white text-base">
                    <i class="fa-solid fa-xmark"></i>
                  </div>
                  <div v-else class="w-full h-full relative">
                    <img class="rounded-full" src="/static/roundtable_options_create/genie_icon.svg" />
                  </div>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
        <div>
          <div v-if="currentSubmission.start_date" class="flex items-center space-x-2">
            <button
              @click="handleSubmit()"
              class="sample px-4 py-2 flex justify-between bg-primary hover:bg-primary-dark rounded-lg cursor-pointer text-white disabled:opacity-50"
              :disabled="prompt_response === '' || (prompt_response === currentSubmission.prompt_response && isUpdating === false)"
            >
              {{ isUpdating === true ? 'Updating' : 'Update' }}
              <div class="loader" v-show="isUpdating"></div>
            </button>
          </div>
          <div v-if="!currentSubmission.start_date" class="flex space-x-2 items-center">
            <button
              @click="handleSaveSubmissionContent({ updateRT: true })"
              class="sample px-5 py-1.5 border rounded cursor-pointer bg-gray-200 text-gray-500 disabled:opacity-50"
              :disabled="is_saving || !recent_changes || isSavingResponse || isSavingResponse_autoSave"
            >
              {{ isSavingResponse === true || isSavingResponse_autoSave === true ? 'Saving...' : !recent_changes ? 'Saved' : 'Save' }}
              <i v-if="isSavingResponse === true || isSavingResponse_autoSave === true" class="fa-solid fa-cloud-arrow-up"></i>
            </button>
            <button
              @click="show_confirm_submit_modal = true"
              :disabled="isEmpty || is_generating_feedback === true || is_regenerating === true"
              class="sample flex px-5 py-1.5 bg-green rounded cursor-pointer text-white disabled:opacity-50"
            >
              {{ isSubmitting === false ? 'Submit' : 'Submitting' }}
              <div class="loader" v-show="isSubmitting"></div>
            </button>
          </div>
        </div>
      </div>
    </div>
    <pl-dialog
      title="Submit Response"
      size="confirm_size"
      v-if="show_confirm_submit_modal"
      @close="show_confirm_submit_modal = false"
      :disableOutsideClick="true"
    >
      <div class="h-full w-full overflow-y-auto py-4">
        <div class="flex flex-col h-full">
          <div class="overflow-y-auto h-full px-5 mb-4">
            <div>
              Want to submit your
              <span class="font-semibold">response?</span> You can
              <span class="font-semibold">edit</span> any time!
            </div>
          </div>
          <div class="flex flex-col px-5 flex-grow h-full">
            <div class="flex items-end space-x-2 h-full">
              <button
                @click="show_confirm_submit_modal = false"
                class="bg-gray-100 py-2 flex items-center justify-center space-x-2 text-blueGray-500 font-medium w-full rounded-md border border-solid border-gray-200"
              >
                <span>Cancel</span>
              </button>
              <button
                @click="saveAndPublish()"
                class="bg-green py-2 flex items-center justify-center space-x-2 text-white font-semibold w-full rounded-md border border-solid border-green"
              >
                <span>Yes, Submit</span>
                <div v-if="isSubmitting === true" class="loader"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </pl-dialog>
  </div>
</template>

<script>
  import Vue from 'vue';
  import { mapGetters } from 'vuex';
  import { debounce } from 'lodash';
  import store from 'store';
  import { EventBus } from '../../../../utils/event-bus';
  import { environment } from 'services/apiConfigs';
  /* import wordCountService from '../../services/wordCount/wordCount'; */
  import roundtableService from 'features/onlineRoundtables/onlineRoundtables.services';
  import googleClassroomService from 'services/googleClassroom/googleClassroomServices';
  import adminServices from 'services/admin/adminServices';
  import urlConfigs from 'services/apiConfigs';
  import Editor from '@tinymce/tinymce-vue';

  export default Vue.extend({
    components: {
      Editor,
      TextEditor: () => import('components/textEditor/tinymce.vue'),
      Tooltip: () => import('../../../../components/Tooltip.vue'),
      PointDescriptions: () => import('components/pointDescriptions.vue'),
      ViewCriteriaModal: () =>
        import('../../../Criterias/ViewCriteriaModal.vue'),
      Popper: () => import('vue-popperjs'),
    },
    props: {
      submissionData: {
        type: Promise,
        default: {},
      },
      promptResponse: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        editorMCE: {
          menubar: false,
          branding: false,
          resize: false,
          disabled: true,
          elementpath: false,
          placeholder: '',
          paste_data_images: false,
          automatic_uploads: false,
          paste_block_drop: true,
          paste_block_img_drop: true,
          toolbar_items_size: 'small',
          browser_spellcheck: true,
          emoticons_append: {
            custom_mind_explode: {
              keywords: ['brain', 'mind', 'explode', 'blown'],
              char: '🤯',
            },
          },
          toolbar: 'fullscreen | undo redo | bold underline | alignleft aligncenter | removeformat | numlist bullist | formatselect | emoticons',
          plugins: 'link media autolink advlist lists lists code emoticons fullscreen table wordcount nonbreaking',
          nonbreaking_force_tab: true,
          noneditable_noneditable_class: 'mceNonEditable',
          block_formats: 'Body=p;Header 1=h2;Header 2=h3;',
          
          skin_url: '/static/tinymce/tiny5skin/skins/ui/tiny5skin',
          media_alt_source: false,
          media_poster: false,
          media_dimensions: false,
          link_title: false,
          default_link_target: '_blank',
          target_list: false,
          contextmenu: false,
          toolbar_drawer: false,
          toolbar_mode: 'sliding',
          statusbar: false,
          content_style: `
            body { 
              padding-left: 12px; padding-right: 12px; 
              outline: none !important;
              padding-bottom: 3px;
            } 
            body:focus { 
              outline: none !important; 
            } 
            `,
            setup: function(editor) {
              editor.on('init', function() {
                var contentContainer = editor.getContentAreaContainer();
                if (contentContainer) {
                  var iframe = contentContainer.querySelector('iframe');
                  if (iframe) {
                    iframe.style.outline = 'none';
                    iframe.style.boxShadow = 'none';
                  }
                }
              });
              
            }
        },
        is_saving: false,
        is_submitting: false,
        show_confirm_submit_modal: false,
        prompt_response: '',
        recent_changes: false,
        route_to_leave: null,
        editor: null,
        isEnableToEdit: false,
        isEmpty: true,
        isGenieOpen: false,
        isSavingResponse: false,
        isSavingResponse_autoSave: false,
        isToExecuteDebounceFunction: true,
        timerCount: 0,
        isTimerEnable: false,
        isUpdating: false,
        isSubmitting: false,
        last_genie_feedback: null,
        is_generating_feedback: false,
        is_regenerating: false,
        is_enable_to_regenarate: false,
        show_feedback: false,
        generated_feedback: false,
        genie_feedback: {
          overall_feedback: "",
          strengths: [],
          improvments: []
        },
        prompt_response_new: "",
        debounceMethod: debounce(async () => {
          if (this.isToExecuteDebounceFunction !== false) {

            await this.handleSaveSubmissionContent({ updateRT: false });
          }
        }, 2700),
      };
    },
    watch: {
      isTimerEnable: function (newVal) {
        newVal === false ? (this.timerCount = 60) : (this.timerCount = 30);
      },
      timerCount: {
        handler(value) {
          if (value > 0 && value <= 30) {
            setTimeout(() => this.timerCount--, 1000);
            return;
          }
          if (value === 0 && this.prompt_response !== this.currentSubmission.draft_response) {
            this.timerCount = 0;
            if (this.isSavingResponse !== true) {
              this.handleSaveSubmissionContent({ updateRT: true });
            }
            return;
          }
          if (value > 30 && value < 60) {
            this.isToExecuteDebounceFunction = true;
            return;
          }
        },
      },
      is_submission_saved: function (status) {
        EventBus.$emit('draftResponseNotSaved', status);
        return;
      },
      is_submission_edited_published: function (status) {
        if (this.currentSubmission.is_started === true) {
          EventBus.$emit('editPromptNotSaved', status);
          return;
        }
      },
      isSavingResponse: function (value) {
        if (value === true) {
          this.debounceMethod.cancel();
        }
      },
      '$route.name': function () {
        window.removeEventListener('beforeunload');
      },
      editor(newEditor) {
        if (!newEditor) return;

        this.$emit('wordcount', this.getWordCount());

        newEditor.on('wordcountupdate', () => {
          this.getWordCount();
        });

        newEditor.on('input undo redo change', () => {
          console.log('3', this.getWordCount());
          this.$emit('wordcount', this.getWordCount());
        });
      },
    },
    computed: {
      ...mapGetters([
        'getCurrentRT',
        'getMyRTUserDetails',
        'getCourseDetail',
        'getProfile',
        'getMySubmission',
      ]),
      /* getPromptWordsLength() {
        return wordCountService.getWordCount(this.submissionData);
      }, */
      currentSubmission: function () {
        return this.getCurrentRT.submissions.find(
          (s) => s.id === this.getMyRTUserDetails.submission_id
        );
      },
      hasContent() {
        const text = this.prompt_response
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/g, ' ')
          .trim();
        return text.length > 0;
      },
      
      hasHelpCrunch() {
        return (
          this.getProfile.role === 'teacher' && environment !== 'development'
        );
      },
      /* googleRT() {
        return this.getCurrentRT.google_classroom_course_work_id;
      }, */
      is_submission_saved: function () {
        if (this.currentSubmission.prompt_response === null) {
          if (this.prompt_response !== this.currentSubmission.draft_response) {
            /* Verify why draft response comes null , if not implication run script to change all nulls to empty string and set default value the next verification is stupid */
            if (
              this.currentSubmission.draft_response === null &&
              this.prompt_response === ''
            ) {
              return true;
            }
            return false;
          }
          return true;
        }
      },
      is_submission_edited_published: function () {
        if (this.prompt_response !== this.currentSubmission.prompt_response) {
          return false;
        }
        return true;
      },
      prompt_response_validation() {
        let prompt_text = '';
        if (this.prompt_response) {
          prompt_text = this.prompt_response
            .replace(/<(style|script|iframe)[^>]*?>[\s\S]+?<\/\1\s*>/gi, '')
            .replace(/<[^>]+?>/g, '')
            .replace(/\s+/g, ' ')
            .replace(/ /g, ' ')
            .replace(/\s/g, '')
            .replace(/>/g, ' ');
        }
        const text = prompt_text.split('&nbsp;');
        let is_empty = true;
        text.forEach((el) => {
          if (el !== '') is_empty = false;
        });
        is_empty = false;
        return !this.prompt_response || this.prompt_response === '' || is_empty;
      },
    },
    methods: {
      handleGenieFeedback: async function () {
        this.show_feedback = !this.show_feedback;
        EventBus.$emit('handleOpenGenieFeedback');
      },
      handlePlayTimer: function () {
        this.isTimerEnable = true;
      },
      handleStopTimer: function () {
        this.isTimerEnable = false;
      },
      handleEnableEditionSubmission: function (value) {
        this.isEnableToEdit = value;
        const editor = editor;
        console.log('editor', editor);
        if (editor) {
          editor.options.set('disabled', false);
        }
        /* if (value === false) {
          this.$emit('messageDraftResponse', '');
          EventBus.$emit('editPromptNotSaved', true);
          return;
        } */
        this.$emit('enableEditSubmission', value);
        /* this.$emit('submissionChanged', false); */
      },
      handleResponseChange: async function (prompt_response_on_written) {
        if (this.getCurrentRT.is_genie_feedback_enable === true && this.currentSubmission.submission_history.length > 0) {
          const words_last_feedback = this.currentSubmission.submission_history[0].prompt_response ? this.currentSubmission.submission_history[0].prompt_response.replace(/<[^>]*>/g, '') : '';
          const words_current = prompt_response_on_written.replace(/<[^>]*>/g, '');
          this.is_enable_to_regenarate = this.getPermissionToRegenarateFeedback(words_last_feedback,words_current);
        }

        this.$emit('submissionChanged', false);
        this.$emit('changingPrompt', prompt_response_on_written);
        this.prompt_response = prompt_response_on_written;
        this.recent_changes = true;
        this.isEmpty = this.prompt_response_validation;

        /* This is a observer ---------------------------------------------------------- */
        if (this.currentSubmission.by_pass_date !== null) {
          /* He dont have a submission ------------------------------------------------- */
          if (this.currentSubmission.prompt_response === null) {
            this.handlePlayTimer();
            if (this.currentSubmission.draft_response !== prompt_response_on_written) {
              this.$emit('messageDraftResponse', 'Your answer has not been saved yet...');
            }
            if (this.currentSubmission.draft_response === prompt_response_on_written) {
              this.recent_changes = false;
              setTimeout(() => this.$emit('messageDraftResponse', ''), 5000);
              this.$emit('messageDraftResponse', 'No changes to save!');
            }
            if (prompt_response_on_written !== this.currentSubmission.prompt_response) {
              const payload = {
                draft_response: prompt_response_on_written,
                submission_id: this.getMyRTUserDetails.submission_id,
                word_count: this.getWordCount(),
              };
              this.debounceMethod(payload);
            }
          }
          /* He have a submission -------------------------------------------------------- */
          if (this.currentSubmission.prompt_response !== null) {
            if (this.currentSubmission.prompt_response !== prompt_response_on_written) {
              this.$emit('messageDraftResponse', 'Your changes has not been published yet...');
            }
            if (this.currentSubmission.prompt_response === prompt_response_on_written) {
              this.recent_changes = false;
              setTimeout(() => this.$emit('messageDraftResponse', ''), 5000);
              this.$emit('messageDraftResponse', 'No changes to publish!');
            }
          }
          if (prompt_response_on_written !== this.currentSubmission.prompt_response) {
            this.$emit('submissionChanged', true);
            this.$emit('changingPrompt', prompt_response_on_written);
          }
        }

        /* This is NOT a observer ---------------------------------------------------------- */
        if (this.currentSubmission.by_pass_date === null) {
          /* He dont have a submission ----------------------------------------------------- */
          if (this.currentSubmission.prompt_response === null) {
            this.handlePlayTimer();
            if (this.currentSubmission.draft_response !== prompt_response_on_written) {
              this.$emit('messageDraftResponse','Your answer has not been saved yet...');
            }
            if (this.currentSubmission.draft_response === prompt_response_on_written) {
              this.recent_changes = false;
              setTimeout(() => this.$emit('messageDraftResponse', ''), 5000);
              this.$emit('messageDraftResponse', 'No changes to save!');
            }
            if (prompt_response_on_written !== this.currentSubmission.prompt_response) {
              const payload = {
                draft_response: prompt_response_on_written,
                submission_id: this.getMyRTUserDetails.submission_id,
                word_count: this.getWordCount(),
              };
              this.debounceMethod(payload);
            }
          }
          /* He have a submission ---------------------------------------------------------- */
          if (this.currentSubmission.prompt_response !== null) {
            if (this.currentSubmission.prompt_response !== prompt_response_on_written) {
              this.$emit('messageDraftResponse','Your changes has not been published yet...');
            }
            if (this.currentSubmission.prompt_response === prompt_response_on_written) {
              this.recent_changes = false;
              setTimeout(() => this.$emit('messageDraftResponse', ''), 5000);
              this.$emit('messageDraftResponse', 'No changes to publish!');
            }
            if (prompt_response_on_written !== this.currentSubmission.prompt_response) {
              this.$emit('submissionChanged', true);
              this.$emit('changingPrompt', prompt_response_on_written);
            }
          }
        }
      },
      handleSaveSubmissionContent: async function (params) {
        const MAX_WORDS = 3500;
        const wordCount = this.getWordCount();

        if (this.isEnableToEdit || this.isSavingResponse) {
          return;
        }

        if (wordCount > MAX_WORDS) {
          EventBus.$emit('createToast', {type: 'Error', message: 'Your answer is too long! Please shorten it to 3500 words or less.', time: 5000});
          return;
        }

        this.isSavingResponse = true;

        const { updateRT } = params;
        this.handleStopTimer();

        const IMG_DATA_URI_RE = /<img[^>]+src=["']data:[^"']*["'][^>]*>/gi;
        const STYLE_DATA_URI_RE = /url\(\s*(['"]?)data:[^)]+\1\s*\)/gi;

        this.prompt_response = this.prompt_response.replace(IMG_DATA_URI_RE, '').replace(STYLE_DATA_URI_RE, '');

        const payload = {
          draft_response: this.prompt_response,
          submission_id: this.getMyRTUserDetails.submission_id,
          word_count: wordCount,
        };

        try {
          await store.dispatch('editSubmissionContent', payload);

          this.recent_changes = false;

          this.$emit('submissionChanged', false);
          setTimeout(() => this.$emit('messageDraftResponse', ''), 3000);

          this.$emit('messageDraftResponse', 'Your answer has been saved...');

          if (updateRT === true) {
            const roundtable = await roundtableService.loadRoundtable(this.$route.params.roundtableID);
            this.$store.commit('UPDATE_RT_DETAILS', { ...roundtable, is_sandbox: false });
          }

          this.isSavingResponse = false;
        } catch (error) {
          console.error('Error saving submission content:', error);
          this.isSavingResponse = false;
          EventBus.$emit('createToast', { type: 'Error', message: 'Something happened saving your response! Please try again or contact support!', time: 5000 });
        }
      },
      saveAndPublish: async function () {
        /* NOTE: Set this to false to not execute auto-save debounce function */

        if (this.getWordCount() > 3500) {
          EventBus.$emit('createToast', {type: 'Error', message: 'Your answer is too long! Please shorten it to 3500 words or less.', time: 5000});
          this.show_confirm_submit_modal = false;
          return;
        }

        this.isSubmitting = true;
        this.isToExecuteDebounceFunction = false;
        this.recent_changes = false;

        const IMG_DATA_URI_RE = /<img[^>]+src=["']data:[^"']*["'][^>]*>/gi;
        const STYLE_DATA_URI_RE = /url\(\s*(['"]?)data:[^)]+\1\s*\)/gi;

        const prompt_response = this.prompt_response.replace(IMG_DATA_URI_RE, '').replace(STYLE_DATA_URI_RE, '');
        
        const payload = {
          prompt_response: prompt_response,
          word_count: this.getWordCount(),
          refreshCourseDetails: false,
          draft_response: this.prompt_response,
        };
        const payloadEdit = {
          draft_response: this.prompt_response,
          submission_id: this.getMyRTUserDetails.submission_id,
          word_count: this.getWordCount(),
        };
        await store.dispatch('editSubmissionContent', payloadEdit);
        const isSubmitted = await this.$store.dispatch('submitResponse', payload);
        if (isSubmitted.status === 200) {
          this.$emit('updateDiscussion');
          this.isSubmitting = false;
          if (this.getCurrentRT.google_classroom_course_work_id && this.submissionData.google_classroom_integration && this.submissionData.google_classroom_integration.connected && !this.submissionData.google_classroom_integration.submission_id) {
            this.submitGoogleClassroomSubmission();
          }
          this.submitIntegrationSubmission();
          this.show_confirm_submit_modal = false;
          return;
        }
        const toast = {
          type: 'Error',
          message: 'Something happened! Please try again!',
          time: 3500,
        };
        return setTimeout(() => EventBus.$emit('createToast', toast), 1500);
      },
      submitGoogleClassroomSubmission: async function () {
        const link = `${urlConfigs.uiURL}/classes/${this.getCourseDetail.id}/roundtables/online/${this.getCurrentRT.id}/?submissionID=${this.submissionData.id}&tab=comments`;
        const payload = { link };
        await googleClassroomService.createGoogleClassroomAssignmentSubmission(
          this.getCourseDetail.id,
          this.getCourseDetail.google_classroom_integration.course_id,
          'written',
          this.getCurrentRT.id,
          this.getCurrentRT.google_classroom_course_work_id,
          payload
        );
      },
      submitIntegrationSubmission: async function () {
        const { edlink_integrations: course_edlink_integrations } =
          this.getCourseDetail;
        const url = `${urlConfigs.uiURL}/classes/${this.getCourseDetail.id}/roundtables/online/${this.getCurrentRT.id}/?submissionID=${this.submissionData.id}&tab=comments`;
        const payload = {
          body: `<div style="margin-left:20px ">Click the link below to see this student submission inside of Parlay:</div>
          <br>
          <a href="${url}"
          <span style="margin-left: 20px;font-weight: bold;"> Parlay Submission </span>
          </a>
          </div>`,
          url, // it's necessary for canvas integration
          type: 'text', // it's necessary for canvas integration
        };
        const { edlink_integrations } = this.submissionData;
        sessionStorage.setItem('course_id', this.getCourseDetail.id);
        sessionStorage.setItem('activity_id', this.getCurrentRT.id);
        sessionStorage.setItem('activity_type', 'online');
        const course_id = this.getCourseDetail.id;
        const redirect_uri = `${urlConfigs.uiURL}/dashboard`;
        if (edlink_integrations && edlink_integrations.length) {
          for (let j = 0; j < edlink_integrations.length; j += 1) {
            const integration = edlink_integrations[j];
            const course_integration = course_edlink_integrations.find(
              (x) => x.id === integration.id
            );
            const { course_id: edlink_course_id } = course_integration;
            const user_integration = this.getProfile.edlink_integrations.find(
              (x) => x.id === integration.id
            );
            const rt_integration = this.getCurrentRT.edlink_integrations.find(
              (int) => int.id === integration.id
            );
            if (rt_integration) {
              const { id: integration_id, assignment_id } = rt_integration;
              if (!user_integration) {
                this.$store
                  .dispatch('getSsoCourseMetadata', { course_id, redirect_uri })
                  .then((res) => {
                    if (res.sso_login_url) {
                      window.open(res.sso_login_url, '_self');
                    }
                  });
              }
              await adminServices.createSsoCourseAssignmentSubmission(
                course_id,
                integration_id,
                edlink_course_id,
                'written',
                this.getCurrentRT.id,
                assignment_id,
                payload
              );
            }
          }
        }
        sessionStorage.removeItem('course_id');
        sessionStorage.removeItem('activity_id');
        sessionStorage.removeItem('activity_type');
      },
      handleSubmit: async function () {
        const MAX_WORDS = 3500;
        const wordCount = this.getWordCount();

        if (wordCount > MAX_WORDS) {
          EventBus.$emit('createToast', {type: 'Error', message: 'Your answer is too long! Please shorten it to 3500 words or less.', time: 5000});
          return;
        }

        if (this.prompt_response_validation === false && this.prompt_response !== this.currentSubmission.prompt_response) {

          const IMG_DATA_URI_RE = /<img[^>]+src=["']data:[^"']*["'][^>]*>/gi;
          const STYLE_DATA_URI_RE = /url\(\s*(['"]?)data:[^)]+\1\s*\)/gi;

          const prompt_response = this.prompt_response.replace(IMG_DATA_URI_RE, '').replace(STYLE_DATA_URI_RE, '');

          this.isUpdating = true;
          const payload = {
            prompt_response: prompt_response,
            submission_id: this.getMyRTUserDetails.submission_id,
            word_count: this.getWordCount(),
            submit_edit_submission: true,
          };

          try { 
            await this.$store.dispatch('editSubmissionContent', payload);
            this.is_saving = false;
            this.recent_changes = false;
            
            EventBus.$emit('createToast', { type: 'Success', message: 'Submission saved with success!', time: 5000 });
            this.isUpdating = false;
            this.$emit('updateDiscussion');
            
          } catch (error) {
            console.error('Error saving the submission content');
            EventBus.$emit('createToast', { type: 'Error', message: 'Something Happened! Please try again.', time: 5000 });
          }
        }
      },
      getWordCount() {
        if (this.editor && this.editor.plugins && this.editor.plugins.wordcount) {
          return this.editor.plugins.wordcount.getCount();
        }
        return 0;
      },
      getPermissionToRegenarateFeedback(last_prompt, current_prompt) {
        const len1 = last_prompt.length;
        const len2 = current_prompt.length;
        const diff = Math.abs(len1 - len2);
        return diff > 100 || diff < -100;
      },
    },
    mounted: async function () {
      const { roundtableID } = this.$route.params;
      this.$route.query.enable_editing === true ? (this.isEnableToEdit = true) : (this.isEnableToEdit = false);
      if (this.currentSubmission.is_started === false) {
        this.$emit('enableEditSubmission', true);
      }
      /* Improve this - just to test */
      /* const submissions_history = await Axios.get(`/api/submissions/history/${this.currentSubmission.id}`);
      console.log(submissions_history, 'submissions_history');
      console.log(this.currentSubmission, 'submission'); */

      // Set up the prompt response from the state
      if (this.submissionData.prompt_response) {
        this.prompt_response = this.submissionData.prompt_response;
      } else {
        this.prompt_response = this.promptResponse;
      }

      this.prompt_response_new = this.currentSubmission.prompt_response !== null ? this.currentSubmission.prompt_response : this.currentSubmission.draft_response;

      /* if(this.hasTeacherPermissionsInCourse === false) {
        this.$store.dispatch('openOnlineRoundtable', { id: this.getMyRTUserDetails.submission_id, rtId: roundtableID });
      } */
      /* this.currentSubmission.opened !== true */
      this.$emit('changingPrompt', this.prompt_response);
      await this.$store.dispatch('myRTIdentity', roundtableID);
      this.prompt_response =
        this.currentSubmission.prompt_response === null &&
        this.currentSubmission.draft_response !== null
          ? this.currentSubmission.draft_response
          : this.currentSubmission.prompt_response !== null
          ? this.currentSubmission.prompt_response
          : '';
      let prompt_text = '';
      if (this.prompt_response) {
        prompt_text = this.prompt_response
          .replace(/<(style|script|iframe)[^>]*?>[\s\S]+?<\/\1\s*>/gi, '')
          .replace(/<[^>]+?>/g, '')
          .replace(/\s+/g, ' ')
          .replace(/ /g, ' ')
          .replace(/\s/g, '')
          .replace(/>/g, ' ');
      }
      const text = prompt_text.split('&nbsp;');
      text.forEach((el) => {
        if (el !== '') this.isEmpty = false;
      });

      const parser = new DOMParser();
      const doc = parser.parseFromString(this.prompt_response, 'text/html');
      this.parsed_response_text = doc.documentElement.innerText;

      window.addEventListener('beforeunload', (e) => {
        if (
          this.is_submission_saved === false &&
          this.currentSubmission.is_started === false
        ) {
          var confirmationMessage = 'something';
          e.returnValue = confirmationMessage;
          return confirmationMessage;
        }
      });

      EventBus.$on('force_draft_save',
        async ({ route_to_go, update_RT }) => {
          await this.handleSaveSubmissionContent({ updateRT: update_RT });
          EventBus.$emit('draftResponseNotSaved', true);
          if (route_to_go !== null) {
            this.route_to_leave = { name: route_to_go.name, params: route_to_go.params, query: route_to_go.query };
            this.$router.push(this.route_to_leave);
          }
        }
      );

      EventBus.$on('handle_stop_save_submission', async () => {
        this.isToExecuteDebounceFunction = false;
      });

      EventBus.$on('handleCloseGenieFeedback', () => {
        this.show_feedback = false;
      });

      EventBus.$on('forceLeaveEdit', async (route_to_go) => {
        if (this.currentSubmission.prompt_response !== null) {
          if (route_to_go.isTab === false) {
            EventBus.$emit('editPromptNotSaved', true);
            this.$router.push({
              name: route_to_go.route.name,
              params: route_to_go.route.params,
              query: route_to_go.route.query,
            });
            return;
          }
          EventBus.$emit('editPromptNotSaved', true);
          if (route_to_go.isTab === true) {
            EventBus.$emit('handleTab', route_to_go.route);
          }
          return;
        }
      });
      setTimeout(() => {
        if (this.hasHelpCrunch) {
          let statusbar = document.getElementsByClassName('tox-statusbar')[0];
          if (statusbar) statusbar.style.height = '7%';
        }
      }, 500);
    },
    created: async function () {
      return window.addEventListener('beforeunload', this.handler);
    },
    beforeDestroy() {
      EventBus.$off('force_draft_save');
      EventBus.$off('forceLeaveEdit');
      window.removeEventListener('beforeunload', this.handler);
    },
  });
</script>
<style scoped lang="stylus">
  @import '~app/global-stylus'
  .genie-feedback
    &&::-webkit-scrollbar-thumb
      background: rgb( 255, 152, 192);
    &&::-webkit-scrollbar-thumb:hover
      background: rgb(255, 34, 122);
    &&::-webkit-scrollbar
      width: 5px;
</style>
<style scoped lang="scss">
  .submission-editor h2 {
    font-size: 24px;
    font-weight: 700;
    margin: 20px 0;
  }
  .submission-editor h3 {
    font-size: 18px;
    font-weight: 700;
    margin: 14px 0;
  }
  .submission-editor p {
    font-size: 14px;
    margin: 10px 0;
  }
  .submission-editor ul {
    list-style-type: disc;
  }
  .submission-editor ol {
    list-style-type: decimal;
  }
  .submission-editor b {
    font-weight: 700;
  }
  .submission-editor strong {
    font-weight: 700;
  }
  .submission-editor table {
    border-collapse: collapse;
    border-width: 1px;
    --tw-border-opacity: 1;
    border-color: rgba(161, 161, 170, var(--tw-border-opacity));
  }
  .submission-editor td {
    border-width: 1px;
    --tw-border-opacity: 1;
    border-color: rgba(212, 212, 216, var(--tw-border-opacity));
  }
  .submission-editor a {
    color: blue;
    text-decoration: underline;
  }
  .height-form {
    height: calc(100vh - 160px);
  }
  .popper-feedback {
    max-height: 50%;
  }
  .loader,
  .loader:after {
    border-radius: 50%;
    width: 15px;
    height: 15px;
  }
  
  .loader {
    border-top: 3px solid rgba(255, 255, 255, 0.2);
    border-right: 3px solid rgba(255, 255, 255, 0.2);
    border-bottom: 3px solid rgba(255, 255, 255, 0.2);
    border-left: 3px solid #ffffff;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: load8 1.1s infinite linear;
    animation: load8 1.1s infinite linear;
  }
  @-webkit-keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
  @keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
</style>


// WEBPACK FOOTER //
// submissionForm.vue?3ce43ab6